'use client';

import { useState } from 'react';
import { useCookies } from 'react-cookie';
import { motion, AnimatePresence } from 'framer-motion';

interface CookieSettingsProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CookieSettings({ isOpen, onClose }: CookieSettingsProps) {
  const [cookies, setCookie] = useCookies<any>(['cookieConsent', 'cookiePreferences']);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always required
    functional: cookies.cookiePreferences?.functional || false,
    analytics: cookies.cookiePreferences?.analytics || false,
    marketing: cookies.cookiePreferences?.marketing || false
  });

  const savePreferences = () => {
    // Save individual preferences
    setCookie('cookiePreferences', preferences, { path: '/', maxAge: 60 * 60 * 24 * 365 });
    
    // Set the main consent cookie based on selections
    if (preferences.functional || preferences.analytics || preferences.marketing) {
      setCookie('cookieConsent', 'custom', { path: '/', maxAge: 60 * 60 * 24 * 365 });
    } else {
      setCookie('cookieConsent', 'necessary', { path: '/', maxAge: 60 * 60 * 24 * 365 });
    }
    
    onClose();
  };

  const handleToggle = (key: keyof typeof preferences) => {
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-[#1A1A1D] text-white rounded-lg shadow-2xl w-full max-w-lg overflow-hidden"
          >
            <div className="p-6">
              <h2 className="text-2xl font-bold mb-4">Cookie Settings</h2>
              <p className="text-sm text-gray-300 mb-6">
                Customize your cookie preferences. Some cookies are necessary for the website to function properly.
              </p>

              <div className="space-y-4 mb-6">
                {/* Necessary Cookies */}
                <div className="flex items-center justify-between p-4 bg-neutral-800 rounded-md">
                  <div>
                    <h3 className="font-medium text-white">Necessary Cookies</h3>
                    <p className="text-xs text-gray-400 mt-1">Required for the website to function</p>
                  </div>
                  <div className="relative">
                    <input 
                      type="checkbox" 
                      checked={preferences.necessary} 
                      disabled={true}
                      className="opacity-60 cursor-not-allowed h-5 w-5 rounded" 
                    />
                  </div>
                </div>

                {/* Functional Cookies */}
                <div className="flex items-center justify-between p-4 bg-neutral-800 rounded-md">
                  <div>
                    <h3 className="font-medium text-white">Functional Cookies</h3>
                    <p className="text-xs text-gray-400 mt-1">Enhance functionality and personalization</p>
                  </div>
                  <div className="relative">
                    <input 
                      type="checkbox" 
                      checked={preferences.functional} 
                      onChange={() => handleToggle('functional')}
                      className="h-5 w-5 rounded accent-[#F1EFEC]" 
                    />
                  </div>
                </div>

                {/* Analytics Cookies */}
                <div className="flex items-center justify-between p-4 bg-neutral-800 rounded-md">
                  <div>
                    <h3 className="font-medium text-white">Analytics Cookies</h3>
                    <p className="text-xs text-gray-400 mt-1">Help us improve by tracking how you use the site</p>
                  </div>
                  <div className="relative">
                    <input 
                      type="checkbox" 
                      checked={preferences.analytics} 
                      onChange={() => handleToggle('analytics')}
                      className="h-5 w-5 rounded accent-[#F1EFEC]" 
                    />
                  </div>
                </div>

                {/* Marketing Cookies */}
                <div className="flex items-center justify-between p-4 bg-neutral-800 rounded-md">
                  <div>
                    <h3 className="font-medium text-white">Marketing Cookies</h3>
                    <p className="text-xs text-gray-400 mt-1">Used to deliver relevant advertisements</p>
                  </div>
                  <div className="relative">
                    <input 
                      type="checkbox" 
                      checked={preferences.marketing} 
                      onChange={() => handleToggle('marketing')}
                      className="h-5 w-5 rounded accent-[#F1EFEC]" 
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm border border-neutral-600 text-white rounded-md hover:bg-neutral-800 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={savePreferences}
                  className="px-4 py-2 text-sm bg-[#F1EFEC] text-[#1A1A1D] font-medium rounded-md hover:opacity-90 transition"
                >
                  Save Preferences
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
} 